/**
 * Ref-counted Loro documents with sync, persistence and presence (spec §8.5).
 *
 * Verified against loro-websocket 0.6.2 / loro-adaptors 0.6.1:
 * - `new LoroAdaptor(doc)` wraps an existing LoroDoc.
 * - `client.join({ roomId, crdtAdaptor, auth, onStatusChange })`; `auth` may be a provider
 *   function invoked on the initial join and on every rejoin, which is how tokens are refreshed.
 * - `room.waitForReachingServerVersion()` resolves once the doc holds the server version.
 * - `doc.subscribeLocalUpdates(bytes => ...)` (loro-crdt 1.16) feeds the pending-update log.
 * - Kicks arrive as RoomError Evicted -> room status "error"; the manager re-requests a token
 *   and rejoins (or reports lost access).
 */
import { LoroDoc, type Subscription, EphemeralStore } from "loro-crdt";
import { LoroWebsocketClient, RoomJoinStatus, type LoroWebsocketClientRoom } from "loro-websocket";
import { LoroAdaptor, LoroEphemeralAdaptor } from "loro-adaptors/loro";
import { appendPending, clearPending, installPendingFlush, loadPending, loadSnapshot, saveSnapshot } from "./persistence";
import { roomToken } from "./tokens";

export type DocKind = "page" | "workspace";
export type SyncStatus = "offline" | "connecting" | "syncing" | "synced" | "error" | "forbidden";

export interface DocHandle {
  kind: DocKind;
  crdtDocId: string;
  room: string;
  doc: LoroDoc;
  presence: EphemeralStore;
  mode: "read" | "write" | "unknown";
  status: SyncStatus;
  /** the first join attempt has finished (joined, or failed/offline). Views must not initialise a
   *  document before this: writes made against an unsynced doc win the merge and destroy content. */
  joinSettled: boolean;
  subscribeStatus(cb: (s: SyncStatus) => void): () => void;
  release(): void;
}

interface Entry {
  handle: DocHandle;
  refs: number;
  room?: LoroWebsocketClientRoom;
  presenceRoom?: LoroWebsocketClientRoom;
  localSub?: Subscription;
  statusListeners: Set<(s: SyncStatus) => void>;
  closing?: Promise<void>;
  /** resolves once the IndexedDB restore has run; every opener waits for it */
  restored: Promise<void>;
}

const entries = new Map<string, Entry>();
let client: LoroWebsocketClient | null = null;

function wsUrl(): string {
  const proto = location.protocol === "https:" ? "wss" : "ws";
  return `${proto}://${location.host}/sync`;
}

export function syncClient(): LoroWebsocketClient {
  if (!client) {
    client = new LoroWebsocketClient({ url: wsUrl() });
    client.onStatusChange((s) => {
      for (const e of entries.values()) {
        if (s === "disconnected") setStatus(e, "offline");
        else if (s === "connecting") setStatus(e, "connecting");
      }
    });
  }
  return client;
}

function setStatus(e: Entry, s: SyncStatus) {
  if (e.handle.status === s) return;
  e.handle.status = s;
  for (const cb of e.statusListeners) cb(s);
}

export function roomIdFor(kind: DocKind, id: string): string {
  return kind === "page" ? `doc:${id}` : `ws:${id}`;
}

export async function openDoc(kind: DocKind, id: string): Promise<DocHandle> {
  const room = roomIdFor(kind, id);
  const existing = entries.get(room);
  if (existing) {
    existing.refs += 1;
    // A second opener (React StrictMode's double effect, or two views of one page) must not get the
    // doc before its local state is restored: it would render the empty doc, and a snapshot import
    // into an empty doc followed by a join with nothing new to send never re-renders it.
    await existing.restored;
    return existing.handle;
  }
  // 1. fresh random peer (I10: never setPeerId with a persisted value)
  const doc = new LoroDoc();
  const presence = new EphemeralStore(30_000);
  const statusListeners = new Set<(s: SyncStatus) => void>();
  let markRestored!: () => void;
  const entry: Entry = {
    restored: new Promise<void>((resolve) => (markRestored = resolve)),
    refs: 1,
    statusListeners,
    handle: {
      kind,
      crdtDocId: id,
      room,
      doc,
      presence,
      mode: "unknown",
      status: "offline",
      joinSettled: false,
      subscribeStatus(cb) {
        statusListeners.add(cb);
        cb(entry.handle.status);
        return () => statusListeners.delete(cb);
      },
      release() {
        releaseDoc(room);
      },
    },
  };
  entries.set(room, entry);
  // 2. restore from IndexedDB
  try {
    const snap = await loadSnapshot(room);
    if (snap) doc.import(snap);
    const pending = await loadPending(room);
    if (pending.length) doc.importBatch(pending);
  } catch (e) {
    console.warn("persistence restore failed", e);
  } finally {
    markRestored();
  }
  // 3. persist local updates as they happen (journalled synchronously if the page unloads first)
  installPendingFlush();
  entry.localSub = doc.subscribeLocalUpdates((bytes) => {
    void appendPending(room, bytes);
  });
  // 4. connect (non-blocking; offline editing continues)
  void connectEntry(entry);
  return entry.handle;
}

/** Mark the join attempt finished and wake status subscribers so views can initialise. */
function settleJoin(entry: Entry): void {
  if (entry.handle.joinSettled) return;
  entry.handle.joinSettled = true;
  for (const cb of entry.statusListeners) cb(entry.handle.status);
}

async function connectEntry(entry: Entry): Promise<void> {
  const { handle } = entry;
  const c = syncClient();
  setStatus(entry, "connecting");
  const peerId = handle.doc.peerIdStr;
  const authProvider = async () => {
    const t = await roomToken(handle.room, peerId);
    handle.mode = t.mode;
    return new TextEncoder().encode(t.token);
  };
  try {
    const adaptor = new LoroAdaptor(handle.doc);
    entry.room = await c.join({
      roomId: handle.room,
      crdtAdaptor: adaptor,
      auth: authProvider,
      onStatusChange: (s) => {
        if (s === RoomJoinStatus.Joined) setStatus(entry, "syncing");
        else if (s === RoomJoinStatus.Reconnecting || s === RoomJoinStatus.Connecting) setStatus(entry, "connecting");
        else if (s === RoomJoinStatus.Disconnected) setStatus(entry, "offline");
        else if (s === RoomJoinStatus.Error) {
          console.debug("[docManager] room status error", handle.room);
          // kicked (acl_changed) or auth failure: try once more with a fresh token; while
          // offline just wait for the browser's online event (spec §16.2)
          if (!navigator.onLine) setStatus(entry, "offline");
          else {
            setStatus(entry, "error");
            if (entry.refs > 0) setTimeout(() => void rejoin(entry), 1500);
          }
        }
      },
    });
    entry.presenceRoom = await c.join({ roomId: handle.room, crdtAdaptor: new LoroEphemeralAdaptor(handle.presence), auth: authProvider });
    await entry.room.waitForReachingServerVersion();
    // 6. snapshot + clear acknowledged pending updates
    await saveSnapshot(handle.room, handle.doc.export({ mode: "snapshot" }));
    await clearPending(handle.room);
    setStatus(entry, "synced");
    settleJoin(entry);
    // keep the snapshot fresh on remote changes (debounced)
    let timer: ReturnType<typeof setTimeout> | undefined;
    handle.doc.subscribe((ev) => {
      if (ev.by === "import") {
        clearTimeout(timer);
        timer = setTimeout(() => void saveSnapshot(handle.room, handle.doc.export({ mode: "snapshot" })), 2000);
      }
    });
  } catch (e) {
    const msg = e instanceof Error ? e.message : String(e);
    console.warn(`join ${handle.room} failed: ${msg}`);
    if (!navigator.onLine) setStatus(entry, "offline");
    else setStatus(entry, /403|forbidden|410/i.test(msg) ? "forbidden" : "error");
    settleJoin(entry);
  }
}

async function rejoin(entry: Entry): Promise<void> {
  console.debug("[docManager] rejoin", entry.handle.room, "refs", entry.refs, "status", entry.handle.status);
  if (entry.refs <= 0 || entry.closing || !navigator.onLine) return;
  try {
    await entry.room?.destroy();
  } catch {
    /* ignore */
  }
  entry.room = undefined;
  await connectEntry(entry);
}

function releaseDoc(room: string): void {
  const e = entries.get(room);
  if (!e) return;
  e.refs -= 1;
  if (e.refs > 0) return;
  e.closing = (async () => {
    entries.delete(room);
    e.localSub?.();
    try {
      await saveSnapshot(room, e.handle.doc.export({ mode: "snapshot" }));
    } catch {
      /* ignore */
    }
    await e.presenceRoom?.destroy().catch(() => {});
    await e.room?.destroy().catch(() => {});
  })();
}

// when the browser comes back online, entries that could not join get a fresh attempt
if (typeof window !== "undefined") {
  window.addEventListener("online", () => {
    for (const e of entries.values()) {
      if (e.refs > 0 && !e.room) void rejoin(e);
    }
  });
}

export function openHandles(): DocHandle[] {
  return [...entries.values()].map((e) => e.handle);
}
